'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { defaultLocale, type SupportedLocale, normalizeLocale, rtlLocales, setStoredLocale } from '@/lib/i18n';

/**
 * Server-to-client locale bridge for pages under app/[locale]/.
 *
 * The locale is established at build time from the URL segment. This context
 * lets nested Client Components read the locale without re-detecting from
 * localStorage / Accept-Language / cookies.
 *
 * Used by:
 *   - components/shared/LocaleLink.tsx (prepends /es/, /fr/, etc. to internal hrefs)
 *   - any other Client Component that needs to know which locale page it's on
 */
const LocaleContext = createContext<SupportedLocale>(defaultLocale);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: SupportedLocale;
  children: ReactNode;
}) {
  // Persist the locale so root-only pages (checkout, orders, etc.) can
  // read it back via ClientLocaleProvider and show the same language.
  useEffect(() => {
    setStoredLocale(locale);
  }, [locale]);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

/**
 * ClientLocaleProvider defaults transactional routes under app/(default)/ to
 * English. These routes are root-only; they should not inherit a previously
 * stored marketing-page locale. An explicit ?locale=... remains authoritative
 * for payment-provider return URLs.
 */
export function ClientLocaleProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [locale, setLocale] = useState<SupportedLocale>(defaultLocale);

  useEffect(() => {
    // Switch after hydration so the first client render matches static HTML.
    // URL locale is authoritative for cross-domain payment returns. Browser
    // language and stored preferences are intentionally not used here: default
    // transactional pages are English unless the URL explicitly says otherwise.
    const urlLocale = normalizeLocale(new URLSearchParams(window.location.search).get('locale'));
    if (urlLocale) {
      setLocale(urlLocale);
      setStoredLocale(urlLocale);
    }
  }, []);

  // Update HTML elements on the client side when locale changes.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.lang = locale;
      root.dir = rtlLocales.has(locale) ? 'rtl' : 'ltr';
      root.setAttribute('data-locale', locale);
    }
  }, [locale]);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): SupportedLocale {
  return useContext(LocaleContext);
}
